/**
 * DocumentAnalysisTab - TASK-253
 * Forensic document analysis with AI and reverse image search
 * Ported from prototypes/aml-sentinel/components/DocumentVerifier.tsx
 */

import React, { useState, useRef } from 'react';
import {
  Upload,
  FileText,
  AlertTriangle,
  CheckCircle,
  Search,
  Eye,
  File as FileIcon,
  Loader2,
  Link as LinkIcon,
  ScanFace,
  Download,
} from 'lucide-react';
import {
  analyzeDocument,
  AMLRiskLevel,
  DocumentAnalysisResult,
  ValidationFinding,
} from '../../services/amlService';

export const DocumentAnalysisTab: React.FC = () => {
  const [analyzing, setAnalyzing] = useState(false);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [result, setResult] = useState<DocumentAnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCurrentFile(file);
      setResult(null);
      setError(null);

      // Create preview URL for images
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleAnalyze = async () => {
    if (!currentFile) return;
    setAnalyzing(true);
    setError(null);

    try {
      const data = await analyzeDocument(currentFile, true);
      setResult(data);
    } catch (e) {
      console.error('Document analysis failed:', e);
      setError('Analysis failed. Please try again or check the backend service.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleExportReport = () => {
    if (!result) return;

    const reportText = `
FORENSIC DOCUMENT ANALYSIS REPORT
Date: ${new Date(result.uploadDate).toLocaleString()}
File: ${result.fileName}
------------------------------------------------
OVERALL RISK: ${result.riskLevel} (Score: ${result.riskScore}/100)
DETECTED TYPE: ${result.documentTypeDetected}

EXECUTIVE SUMMARY:
${result.summary}

AUTHENTICITY CHECK:
${result.isStolenOrStock ? '[!] WARNING: Potential stock/stolen image detected.' : '[OK] No direct public matches found.'}
${result.onlineMatches.length > 0 ? `Matches found at:\n${result.onlineMatches.join('\n')}` : ''}

DETAILED FINDINGS:
${result.findings.map((f) => `[${f.severity}] ${f.category}: ${f.description}`).join('\n')}
------------------------------------------------
    `;

    const blob = new Blob([reportText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Forensic_Report_${result.fileName}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getSeverityColor = (level: AMLRiskLevel | string) => {
    switch (level) {
      case 'Critical':
        return 'text-red-700 bg-red-100 border-red-200';
      case 'High':
        return 'text-orange-700 bg-orange-100 border-orange-200';
      case 'Medium':
        return 'text-yellow-700 bg-yellow-100 border-yellow-200';
      case 'Low':
        return 'text-green-700 bg-green-100 border-green-200';
      default:
        return 'text-gray-700 bg-gray-100 border-gray-200';
    }
  };

  return (
    <div className="flex flex-col h-full gap-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Document Corroboration</h3>
          <p className="text-gray-500 mt-1">
            Forensic analysis of client documents for format inconsistencies and tampering.
          </p>
        </div>
        {result && (
          <button
            onClick={handleExportReport}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm font-medium text-sm"
          >
            <Download size={16} />
            Export Report
          </button>
        )}
      </div>

      <div className="flex gap-6 flex-1 min-h-0">
        {/* Left: Upload and Preview */}
        <div className="w-1/3 flex flex-col gap-4">
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors h-64 ${
              currentFile
                ? 'border-blue-300 bg-blue-50/50'
                : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
            }`}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              className="hidden"
              accept="image/png, image/jpeg, application/pdf"
            />
            {currentFile ? (
              previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-h-full max-w-full object-contain rounded shadow-sm"
                />
              ) : (
                <div className="text-center">
                  <FileText size={48} className="text-blue-500 mx-auto mb-2" />
                  <span className="font-medium text-gray-700">{currentFile.name}</span>
                </div>
              )
            ) : (
              <>
                <div className="bg-blue-100 p-4 rounded-full mb-4 text-blue-600">
                  <Upload size={24} />
                </div>
                <h3 className="font-semibold text-gray-700">Click to upload document</h3>
                <p className="text-xs text-gray-400 mt-2 text-center">
                  Supports PDF, JPG, PNG.
                  <br />
                  Analyzes layout, fonts, and metadata.
                </p>
              </>
            )}
          </div>

          {currentFile && (
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium shadow-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {analyzing ? <Loader2 className="animate-spin" size={20} /> : <ScanFace size={20} />}
              {analyzing ? 'Analyzing Forensics...' : 'Analyze Document'}
            </button>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Quick Explanation */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm text-gray-600 space-y-2">
            <p className="font-semibold text-gray-800">Checks Performed:</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>Inconsistent fonts & spacing (Format)</li>
              <li>Spelling in official headers (Content)</li>
              <li>Pixel tampering & AI artifacts (Forensics)</li>
              <li>Reverse Image Search (Authenticity)</li>
            </ul>
          </div>
        </div>

        {/* Right: Analysis Results */}
        <div className="w-2/3 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
          {!result ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <Search size={48} className="mb-4 opacity-20" />
              <p>Upload a document to view forensic analysis.</p>
            </div>
          ) : (
            <div className="flex flex-col h-full">
              {/* Header Stats */}
              <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <div>
                  <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                    <FileIcon size={18} className="text-gray-500" />
                    {result.fileName}
                  </h3>
                  <div className="text-xs text-gray-500 mt-1 flex gap-4">
                    <span>
                      Detected Type:{' '}
                      <span className="font-semibold text-gray-700">
                        {result.documentTypeDetected}
                      </span>
                    </span>
                    <span>Analyzed: {new Date(result.uploadDate).toLocaleTimeString()}</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                      Risk Score
                    </div>
                    <div
                      className={`text-2xl font-black ${
                        result.riskScore > 70
                          ? 'text-red-600'
                          : result.riskScore > 40
                            ? 'text-orange-600'
                            : 'text-green-600'
                      }`}
                    >
                      {result.riskScore}/100
                    </div>
                  </div>
                  <div
                    className={`px-4 py-2 rounded-lg text-sm font-bold border ${getSeverityColor(result.riskLevel)}`}
                  >
                    {result.riskLevel} RISK
                  </div>
                </div>
              </div>

              {/* Content Scroll */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Summary */}
                <div className="prose prose-sm max-w-none text-gray-600">
                  <p className="font-medium text-gray-800 mb-1">Executive Summary</p>
                  {result.summary}
                </div>

                {/* Stolen/Stock Check */}
                <div
                  className={`p-4 rounded-lg border flex items-start gap-3 ${result.isStolenOrStock ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}
                >
                  {result.isStolenOrStock ? (
                    <AlertTriangle className="text-red-600 mt-1" size={20} />
                  ) : (
                    <CheckCircle className="text-green-600 mt-1" size={20} />
                  )}
                  <div>
                    <h4
                      className={`font-bold text-sm ${result.isStolenOrStock ? 'text-red-800' : 'text-green-800'}`}
                    >
                      {result.isStolenOrStock
                        ? 'Potential Stock/Stolen Image Detected'
                        : 'No Public Copies Found'}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1">
                      {result.isStolenOrStock
                        ? 'Reverse search indicates this image exists on public websites or stock photo repositories.'
                        : 'This document appears unique. No direct matches found in public index.'}
                    </p>
                    {result.onlineMatches.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {result.onlineMatches.map((url, idx) => (
                          <a
                            key={idx}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] bg-white px-2 py-1 rounded border border-gray-200 text-blue-600 hover:underline"
                          >
                            <LinkIcon size={10} /> Found Source {idx + 1}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Detailed Findings */}
                <div>
                  <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Eye size={18} /> Detailed Forensic Findings
                  </h4>
                  {result.findings.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No significant issues detected.</p>
                  ) : (
                    <div className="space-y-3">
                      {result.findings.map((finding: ValidationFinding, idx: number) => (
                        <div
                          key={idx}
                          className="flex gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <div
                            className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                              finding.severity === 'Critical' || finding.severity === 'High'
                                ? 'bg-red-500'
                                : finding.severity === 'Medium'
                                  ? 'bg-orange-500'
                                  : 'bg-green-500'
                            }`}
                          />
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                                {finding.category}
                              </span>
                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${getSeverityColor(finding.severity)}`}
                              >
                                {finding.severity}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{finding.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysisTab;
